/**
 * One customer's load emails — settings + contacts (Phase 17 / Task 17.3, DEC-324).
 *
 * Opened from the Operators tab as a full-pane detail (the TankBatteryDetail /
 * RequestorDetail pattern). Everyone who can read the catalog sees it; only
 * MANAGE_HAULER_CATALOG (DISPATCH_MANAGER+, D-04) can change anything — the server is the
 * authority either way.
 *
 * The contact being edited is looked up by id from the LIVE list on every render, so the
 * dialog shows saved assignments immediately instead of the snapshot it was opened with.
 */

import * as React from 'react';
import type { HaulerOperator } from '../sites-operators/hooks';
import { ActiveBadge, Pill } from '../sites-operators/ui-bits';
import { useSiteContacts, type SiteContact } from './api';
import { ContactFormModal } from './ContactFormModal';
import { OperatorNotificationsPanel } from './OperatorNotificationsPanel';
import { buttonStyle, cardStyle, helpText, primaryButtonStyle } from './styles';

function coverage(c: SiteContact): string {
  if (c.coversAllSites) return 'All sites';
  const n = c.assignments.length;
  return n === 1 ? (c.assignments[0]?.name ?? '1 site') : `${String(n)} sites`;
}

export function OperatorLoadEmailsDetail({
  customer,
  canManage,
  onClose,
}: {
  readonly customer: HaulerOperator;
  readonly canManage: boolean;
  readonly onClose: () => void;
}): React.JSX.Element {
  const { data, isLoading, isError } = useSiteContacts(customer.id);
  const contacts = data?.contacts ?? [];
  const [dialog, setDialog] = React.useState<{ open: boolean; contactId: string | null }>({
    open: false,
    contactId: null,
  });
  const editing =
    dialog.contactId === null ? null : (contacts.find((c) => c.id === dialog.contactId) ?? null);

  return (
    <div>
      <button type="button" style={buttonStyle} onClick={onClose}>
        ← Back to operators
      </button>
      <h2 style={{ fontSize: '22px', margin: '16px 0 4px' }}>{customer.resolvedName}</h2>
      <p style={{ ...helpText, marginBottom: '16px' }}>
        Who at this customer gets Haul Confirmation emails, and how often.
      </p>

      <OperatorNotificationsPanel operatorId={customer.id} canManage={canManage} />

      <section aria-labelledby="site-contacts" style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <h3 id="site-contacts" style={{ fontSize: '18px', margin: 0, flex: 1 }}>
            Contacts ({contacts.length})
          </h3>
          {canManage && (
            <button
              type="button"
              style={primaryButtonStyle}
              onClick={(): void => setDialog({ open: true, contactId: null })}
            >
              Add contact
            </button>
          )}
        </div>
        {isLoading && <p style={helpText}>Loading…</p>}
        {isError && (
          <p style={{ ...helpText, color: 'var(--color-danger)' }}>Could not load contacts.</p>
        )}
        {!isLoading && !isError && contacts.length === 0 && (
          <p style={helpText}>
            No contacts yet. Nobody at this customer will get Haul Confirmation emails until you add
            one.
          </p>
        )}
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
          {contacts.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                onClick={(): void => setDialog({ open: true, contactId: c.id })}
                style={{
                  width: '100%',
                  minHeight: '56px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '10px 4px',
                  background: 'none',
                  border: 'none',
                  borderBottom: '1px solid var(--color-border-secondary)',
                  textAlign: 'left',
                  cursor: 'pointer',
                  color: 'inherit',
                  opacity: c.isActive ? 1 : 0.55,
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: '16px', fontWeight: 600 }}>{c.name}</span>
                  {c.role !== null && (
                    <span style={{ color: 'var(--color-text-secondary)' }}> · {c.role}</span>
                  )}
                  <span
                    style={{
                      display: 'block',
                      fontSize: '14px',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {c.email} · {coverage(c)}
                  </span>
                </span>
                {c.emailStatus === 'BOUNCING' && <Pill label="Email bouncing" tone="muted" />}
                {c.emailStatus === 'COMPLAINED' && <Pill label="Marked as spam" tone="muted" />}
                {c.unsubscribedAt !== null && <Pill label="Unsubscribed" tone="muted" />}
                <ActiveBadge active={c.isActive} />
              </button>
            </li>
          ))}
        </ul>
      </section>

      <ContactFormModal
        // An edit whose contact is not (yet) in the list must not fall back to "add" mode.
        open={dialog.open && (dialog.contactId === null || editing !== null)}
        onOpenChange={(open): void => setDialog((d) => ({ ...d, open }))}
        customer={customer}
        contact={editing}
        canManage={canManage}
      />
    </div>
  );
}
